import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import type { ProjectionResult } from '../../model/ProjectionResult';
import { createExecutionDiagnostic, type ExecutionDiagnostic } from '../../model/SketchEntityReferenceDiagnostics';
import { resolveExternalGeometrySourceRefEntityId } from '../../model/ExternalGeometrySourceDescriptor';
import {
	SketchReferenceSourceResolver,
	type ResolvedSketchReferenceSource,
	type ResolvedReferenceSourceEndpointAnchorIds,
} from './SketchReferenceSourceResolver';
import { ArcSupportProjectionPreprocessor } from './preprocessors/ArcSupportProjectionPreprocessor';
import { CircleSupportProjectionPreprocessor } from './preprocessors/CircleSupportProjectionPreprocessor';
import { EllipseArcSupportProjectionPreprocessor } from './preprocessors/EllipseArcSupportProjectionPreprocessor';
import { EllipseSupportProjectionPreprocessor } from './preprocessors/EllipseSupportProjectionPreprocessor';
import { LineSupportProjectionPreprocessor } from './preprocessors/LineSupportProjectionPreprocessor';
import { ParabolaSupportProjectionPreprocessor } from './preprocessors/ParabolaSupportProjectionPreprocessor';
import { SplineSupportProjectionPreprocessor } from './preprocessors/SplineSupportProjectionPreprocessor';
import { StyleSplineSupportProjectionPreprocessor } from './preprocessors/StyleSplineSupportProjectionPreprocessor';
import type {
	SketchEntityReferenceProjectionServiceDependencies,
	SketchEntityReferenceProjectionPort,
	SketchEntityReferenceProjectionServiceInput,
	SketchEntityReferenceProjectionServiceResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceProjectionService';

export type {
	SketchEntityReferenceProjectionPort,
	SketchEntityReferenceProjectionServiceDependencies,
	SketchEntityReferenceProjectionServiceInput,
	SketchEntityReferenceProjectionServiceResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceProjectionService';

/**
 * 转换实体引用投影服务。
 *
 * 说明：
 * 1. 这是 descriptor 到 ProjectionResult 的唯一业务入口；
 * 2. Preview、Create 与 Rebuild 共用同一套来源解析和投影规则；
 * 3. Resolver 负责读取来源快照，Preprocessor 负责投影，本服务负责类型调度与诊断聚合。
 */
export class SketchEntityReferenceProjectionService implements SketchEntityReferenceProjectionPort {
	private readonly sourceResolver: SketchReferenceSourceResolver;
	private readonly lineProjectionPreprocessor: LineSupportProjectionPreprocessor;
	private readonly circleProjectionPreprocessor: CircleSupportProjectionPreprocessor;
	private readonly arcProjectionPreprocessor: ArcSupportProjectionPreprocessor;
	private readonly ellipseProjectionPreprocessor: EllipseSupportProjectionPreprocessor;
	private readonly ellipseArcProjectionPreprocessor: EllipseArcSupportProjectionPreprocessor;
	private readonly parabolaProjectionPreprocessor: ParabolaSupportProjectionPreprocessor;
	private readonly splineProjectionPreprocessor: SplineSupportProjectionPreprocessor;
	private readonly styleSplineProjectionPreprocessor: StyleSplineSupportProjectionPreprocessor;

	/**
	 * @param dependencies 来源运行时与各投影策略。
	 */
	public constructor(private readonly dependencies: SketchEntityReferenceProjectionServiceDependencies) {
		this.sourceResolver = dependencies.sourceResolver ?? new SketchReferenceSourceResolver();
		this.lineProjectionPreprocessor = dependencies.lineProjectionPreprocessor ?? new LineSupportProjectionPreprocessor();
		this.circleProjectionPreprocessor = dependencies.circleProjectionPreprocessor ?? new CircleSupportProjectionPreprocessor();
		this.arcProjectionPreprocessor = dependencies.arcProjectionPreprocessor ?? new ArcSupportProjectionPreprocessor();
		this.ellipseProjectionPreprocessor = dependencies.ellipseProjectionPreprocessor ?? new EllipseSupportProjectionPreprocessor();
		this.ellipseArcProjectionPreprocessor = dependencies.ellipseArcProjectionPreprocessor ?? new EllipseArcSupportProjectionPreprocessor();
		this.parabolaProjectionPreprocessor = dependencies.parabolaProjectionPreprocessor ?? new ParabolaSupportProjectionPreprocessor();
		this.splineProjectionPreprocessor = dependencies.splineProjectionPreprocessor ?? new SplineSupportProjectionPreprocessor();
		this.styleSplineProjectionPreprocessor = dependencies.styleSplineProjectionPreprocessor ?? new StyleSplineSupportProjectionPreprocessor();
	}

	/**
	 * 将来源描述集合投影到目标草图。
	 *
	 * @param input 投影输入。
	 * @returns 投影结果及全部诊断。
	 */
	public project(input: SketchEntityReferenceProjectionServiceInput): SketchEntityReferenceProjectionServiceResult {
		const resolved = this.sourceResolver.resolve({
			descriptors: input.sourceDescriptors,
			runtime: this.dependencies.runtime,
			runtimeTopologySourcesBySourceKey: input.runtimeTopologySourcesBySourceKey,
		});
		const projectionResults: ProjectionResult[] = [];
		const diagnostics: ExecutionDiagnostic[] = [...resolved.diagnostics];

		resolved.resolvedSources.forEach(resolvedSource => {
			const projectionResult = this.projectResolvedSourceToTargetSketch(resolvedSource, input.targetSketch);
			if (projectionResult) {
				const projectionResultWithAnchors = this.attachEndpointAnchorBindings(projectionResult, resolvedSource);
				projectionResults.push(projectionResultWithAnchors);
				diagnostics.push(...projectionResultWithAnchors.diagnostics);
				return;
			}

			diagnostics.push(this.createProjectionDegenerateDiagnostic(resolvedSource));
		});

		return {
			projectionResults,
			diagnostics,
		};
	}
	/**
	 * 把已解析来源携带的端点锚点复制到投影结果。
	 *
	 * @param projectionResult 投影结果。
	 * @param resolvedSource 已解析来源。
	 * @returns 携带端点锚点的投影结果。
	 */
	private attachEndpointAnchorBindings(projectionResult: ProjectionResult, resolvedSource: ResolvedSketchReferenceSource): ProjectionResult {
		const endpointAnchorIds = (
			resolvedSource as ResolvedSketchReferenceSource & {
				endpointAnchorIds?: ResolvedReferenceSourceEndpointAnchorIds;
			}
		).endpointAnchorIds;
		const endpointAnchorBindings = [
			endpointAnchorIds?.start
				? {
						sourceAnchorId: endpointAnchorIds.start,
						targetEndpoint: 'start' as const,
					}
				: null,
			endpointAnchorIds?.end
				? {
						sourceAnchorId: endpointAnchorIds.end,
						targetEndpoint: 'end' as const,
					}
				: null,
		].filter((binding): binding is NonNullable<typeof binding> => Boolean(binding));

		if (endpointAnchorBindings.length <= 0) {
			return projectionResult;
		}

		return {
			...projectionResult,
			endpointAnchorBindings,
		};
	}

	/**
	 * 将单条解析后的来源投影到目标草图。
	 *
	 * @param resolvedSource 已解析的来源几何快照。
	 * @param targetSketch 目标草图。
	 * @returns 成功时返回投影结果，退化时返回 null。
	 */
	private projectResolvedSourceToTargetSketch(resolvedSource: ResolvedSketchReferenceSource, targetSketch: SketchGroup): ProjectionResult | null {
		switch (resolvedSource.sourceKind) {
			case 'line':
				return this.lineProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'circle':
				return this.circleProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'arc':
				return this.arcProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'ellipse':
				return this.ellipseProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'ellipse-arc':
				return this.ellipseArcProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'parabola':
				return this.parabolaProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'spline':
				return this.splineProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
			case 'style-spline':
				return this.styleSplineProjectionPreprocessor.projectToTargetPlane({
					source: resolvedSource,
					targetReferencePlane: targetSketch.referencePlane,
				});
		}
	}

	/**
	 * 为退化投影生成明确诊断。
	 *
	 * @param resolvedSource 已解析来源。
	 * @returns 规范化诊断。
	 */
	private createProjectionDegenerateDiagnostic(resolvedSource: ResolvedSketchReferenceSource): ExecutionDiagnostic {
		const sourceRefEntityId = resolveExternalGeometrySourceRefEntityId(resolvedSource.sourceDescriptor);
		if (resolvedSource.sourceKind === 'circle') {
			return createExecutionDiagnostic({
				kind: 'circle-projection-degenerate',
				level: 'error',
				message: `圆投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'arc') {
			return createExecutionDiagnostic({
				kind: 'arc-projection-degenerate',
				level: 'error',
				message: `圆弧投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'ellipse') {
			return createExecutionDiagnostic({
				kind: 'ellipse-projection-degenerate',
				level: 'error',
				message: `椭圆投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'ellipse-arc') {
			return createExecutionDiagnostic({
				kind: 'ellipse-arc-projection-degenerate',
				level: 'error',
				message: `椭圆弧投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'parabola') {
			return createExecutionDiagnostic({
				kind: 'projection-degenerate',
				level: 'error',
				message: `抛物线投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'spline') {
			return createExecutionDiagnostic({
				kind: 'projection-degenerate',
				level: 'error',
				message: `样条投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		if (resolvedSource.sourceKind === 'style-spline') {
			return createExecutionDiagnostic({
				kind: 'style-spline-projection-degenerate',
				level: 'error',
				message: `样式样条投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
				sourceRefEntityId,
			});
		}

		return createExecutionDiagnostic({
			kind: 'projection-degenerate',
			level: 'error',
			message: `线投影退化，无法生成有效投影结果: ${sourceRefEntityId}`,
			sourceRefEntityId,
		});
	}
}
